import type { UUID } from '@/shared/types/api';

/**
 * Every path in the app, in one place.
 *
 * Parameterised routes come in pairs: the `*Pattern` string is what the router
 * matches on, the function is what call sites navigate with. Nothing outside
 * this file should ever build a path by string concatenation.
 */
export const ROUTES = {
  root: '/',

  // --- Public ---
  login: '/login',

  /** Guided walkthrough for payment-gateway review; needs a demo session. */
  demo: '/demo',

  // --- Onboarding (authenticated, but before any role or membership) ---
  welcome: '/welcome',
  onboardingComplex: '/welcome/complex',
  onboardingBuilding: '/welcome/building',
  onboardingJoin: '/welcome/join',
  onboardingPending: '/welcome/pending',

  // --- Main tabs ---
  home: '/',
  payments: '/payments',
  account: '/account',

  // --- Resident ---
  notifications: '/notifications',
  transactions: '/transactions',
  profile: '/account/profile',
  privacy: '/account/privacy',
  help: '/account/help',

  // --- Payment flow ---
  payChargePattern: '/pay/charge/:unitChargeId',
  payCharge: (unitChargeId: UUID) => `/pay/charge/${unitChargeId}`,
  payOverdue: '/pay/overdue',
  /** Public: the gateway redirects the payer's browser here. */
  paymentCallback: '/payment/callback',

  // --- Management ---
  manage: '/manage',

  manageComplexPattern: '/manage/complexes/:complexId',
  manageComplex: (complexId: UUID) => `/manage/complexes/${complexId}`,

  manageBuildingPattern: '/manage/buildings/:buildingId',
  manageBuilding: (buildingId: UUID) => `/manage/buildings/${buildingId}`,

  manageBuildingUnitsPattern: '/manage/buildings/:buildingId/units',
  manageBuildingUnits: (buildingId: UUID) => `/manage/buildings/${buildingId}/units`,

  manageBuildingRequestsPattern: '/manage/buildings/:buildingId/requests',
  manageBuildingRequests: (buildingId: UUID) => `/manage/buildings/${buildingId}/requests`,

  manageBuildingChargesPattern: '/manage/buildings/:buildingId/charges',
  manageBuildingCharges: (buildingId: UUID) => `/manage/buildings/${buildingId}/charges`,

  manageBuildingManagersPattern: '/manage/buildings/:buildingId/managers',
  manageBuildingManagers: (buildingId: UUID) => `/manage/buildings/${buildingId}/managers`,

  manageBuildingTopPayersPattern: '/manage/buildings/:buildingId/top-payers',
  manageBuildingTopPayers: (buildingId: UUID) => `/manage/buildings/${buildingId}/top-payers`,

  manageBuildingReportPattern: '/manage/buildings/:buildingId/report',
  manageBuildingReport: (buildingId: UUID) => `/manage/buildings/${buildingId}/report`,

  manageMonthlyChargePattern: '/manage/monthly-charges/:monthlyChargeId',
  manageMonthlyCharge: (monthlyChargeId: UUID) => `/manage/monthly-charges/${monthlyChargeId}`,

  manageUnitPattern: '/manage/units/:unitId',
  manageUnit: (unitId: UUID) => `/manage/units/${unitId}`,
} as const;

/** Paths reachable without a session. */
export const PUBLIC_ROUTES: readonly string[] = [ROUTES.login, ROUTES.paymentCallback];
